import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import { createQueryKey } from '@/utils'
import { mapPronunciation } from '@/utils/cmsContentMappings'
import { useCmsContentSearch } from '@/composables/useCmsContentSearch'
import { IbexaContentTypeIdentifier } from '@/enums/IbexaContentTypeIdentifier'
import { useFetchAudioFile } from '@/composables/useFetchAudioFile'
import type { AudioMetadata } from '@/types/Ibexa'
import { useGlossCollectionQuery } from './useGlossCollectionQuery'

export const usePronunciation = (contentId: number) => {
  const { search } = useCmsContentSearch()
  const { fetchAudioFile } = useFetchAudioFile()

  const searchQuery = {
    ['contentTypes[]']: IbexaContentTypeIdentifier.vgsPronunciationBox,
    ['contentId[]']: contentId.toString(),
  }

  const { data } = useQuery({
    queryKey: createQueryKey('pronunciation', contentId),
    queryFn: () => search(searchQuery),
    staleTime: 1000 * 60 * 60 * 8,
  })

  const pronunciation = computed(() => {
    if (!data.value) return
    const pronounciation =  mapPronunciation(data.value[0])
 
    return pronounciation
  })

  const isParentFetched = computed(() => !!data.value)
  const audioId = computed(() => pronunciation.value?.audioFileRelation?.destinationContentId)

  const { data: audio } = useQuery<AudioMetadata | undefined>({
    queryKey: ['pronunciation-audio', audioId],
    queryFn: async () => {
      return pronunciation.value?.audioFileRelation ? await fetchAudioFile(pronunciation.value.audioFileRelation.destinationContentId) : undefined
    },
    staleTime: Infinity,
    enabled: computed(() => Boolean(isParentFetched.value && audioId.value)),
  })

  const hasWord = computed(() => Boolean(pronunciation.value?.wordRelation?.destinationContentId))

  const glossIdInArray = computed(() => hasWord.value ? [Number(pronunciation.value?.wordRelation?.destinationContentId)] : [])
  const { data: words } = useGlossCollectionQuery(glossIdInArray)

  return {
    pronunciation,
    audio,
    word: computed(() => words.value?.[0] ?? undefined),
  }

}
